// @ts-nocheck

import globalBasketUtils from '../../../utils/basket';
import createReducer from '../../../utils/createReducer';
import Basket from '../states/basket';
import Selection from '../states/selection';
import { GridTypes } from '../types';
import type { BasketState } from '../types';
import { GridsActionTypes } from '../types/actions';
import type {
  InitBasketAction,
  UpdateBasketAction,
  OnCheckBoxAction,
  SetBasketErrorAction,
  SwitchTypeAction,
  SetStakeAction,
  CopySystemGridAction,
  RemoveSystemGridAction,
  ClearChecksAction,
  FillRandomlyAction,
  FillPrechecksAction,
  InitPublicBasketAction,
  InitHistoryBasketAction,
  CopyGridAction,
} from '../types/actions';

export const initialState: BasketState = {
  poolId: null,
  gridReference: null,
  gridType: GridTypes.PERSONAL,
  betAmount: '',
  isPrivate: false,
  isSystem: false,
  selections: [],
  isSubmitting: false,
  isValidated: false,
  isHistory: false,
  estr: null,
};

export const initBasket = (state: BasketState, action: InitBasketAction): BasketState => {
  const { poolId, selections } = action.payload;
  const basketFromStorage = Basket.loadFromStorage(poolId);
  if (
    basketFromStorage &&
    basketFromStorage.poolId === poolId &&
    basketFromStorage.selections &&
    basketFromStorage.selections.length > 0
  ) {
    return {
      ...state,
      ...basketFromStorage,
      gridType: basketFromStorage.gridType || GridTypes.PERSONAL,
      selections: basketFromStorage.selections,
      gridReference: null,
      isSubmitting: false,
      isValidated: false,
      isHistory: false,
      estr: null,
    };
  }
  return {
    ...initialState,
    poolId,
    selections,
  };
};

export const initPublicBasket = (state: BasketState, action: InitPublicBasketAction): BasketState => {
  const { poolId, gridReference } = action.payload;
  return {
    ...initialState,
    poolId,
    gridReference,
    gridType: GridTypes.TEAM,
    betAmount: '1',
  };
};

export const initHistoryBasket = (state: BasketState, action: InitHistoryBasketAction): BasketState => {
  const { poolId, gridReference } = action.payload;
  return {
    ...initialState,
    poolId,
    gridReference,
    gridType: GridTypes.TEAM,
    isHistory: true,
    betAmount: '1',
  };
};

export const updateBasket = (state: BasketState, action: UpdateBasketAction): BasketState => {
  const { basket } = action.payload;
  return basket;
};

export const fillPrechecks = (state: BasketState, action: FillPrechecksAction): BasketState => {
  const { selections, poolId } = action.payload;
  return {
    ...initialState,
    poolId,
    gridType: GridTypes.PERSONAL,
    isPrivate: false,
    isSystem: selections.length > 1,
    selections: selections,
  };
};

export const check = (state: BasketState, action: OnCheckBoxAction): BasketState => {
  const { matchIndex, checkType, selectionIndex } = action.payload;
  const { selections } = state;

  let nextSelection = selections[selectionIndex];

  if (nextSelection) {
    nextSelection = Selection.toggleCheck(nextSelection, matchIndex, checkType);
    const nextSelections = [...selections];
    nextSelections[selectionIndex] = nextSelection;
    return {
      ...state,
      selections: nextSelections,
    };
  }
  return state;
};

export const fillRandomly = (state: BasketState, action: FillRandomlyAction): BasketState => {
  const { selectionIndex } = action.payload;
  const { selections } = state;

  let nextSelection = selections[selectionIndex];

  if (nextSelection) {
    nextSelection = Selection.fillRandomly(nextSelection);
    const nextSelections = [...selections];
    nextSelections[selectionIndex] = nextSelection;
    return {
      ...state,
      selections: nextSelections,
    };
  }
  return state;
};

export const setBasketPending = (state: BasketState): BasketState => {
  return {
    ...state,
    isSubmitting: true,
    isValidated: false,
    estr: null,
  };
};

export const setBasketValidated = (state: BasketState): BasketState => {
  return {
    ...state,
    isSubmitting: false,
    isValidated: true,
  };
};

export const setBasketClear = (state: BasketState): BasketState => {
  const { selections } = state;
  const clearSelections = selections[0] ? [Selection.emptySelection(selections[0])] : [];
  return {
    ...state,
    selections: clearSelections,
    isValidated: false,
    isSubmitting: false,
    estr: null,
  };
};

export const setBasketError = (state: BasketState, action: SetBasketErrorAction): BasketState => {
  const { estr } = action.payload;
  return {
    ...state,
    isSubmitting: false,
    estr,
  };
};

export const clearChecks = (state: BasketState, action: ClearChecksAction): BasketState => {
  const { index } = action.payload;
  const { selections } = state;
  const newSelections = [...selections];
  const oldSelection = newSelections[index];
  if (oldSelection) {
    newSelections[index] = Selection.emptySelection(oldSelection);
    return {
      ...state,
      selections: newSelections,
    };
  }
  return state;
};

export const switchType = (state: BasketState, action: SwitchTypeAction): BasketState => {
  const { gridType } = action.payload;
  const { betAmount } = state;
  let newBetAmount = betAmount;
  if (gridType === GridTypes.TEAM && betAmount === '') {
    newBetAmount = '1';
  }
  return {
    ...state,
    gridType,
    betAmount: newBetAmount,
  };
};

export const setStake = (state: BasketState, action: SetStakeAction): BasketState => {
  const { betAmount } = action.payload;
  let finalBetAmount = Math.floor(parseFloat(betAmount)).toString(); // no float allow
  finalBetAmount = isNaN(finalBetAmount) ? '' : finalBetAmount;
  if (betAmount !== state.betAmount && globalBasketUtils.isValidStake(finalBetAmount)) {
    finalBetAmount = globalBasketUtils.formatStake(finalBetAmount);
    return {
      ...state,
      betAmount: finalBetAmount,
    };
  }
  return state;
};

export const togglePrivateMode = (state: BasketState): BasketState => {
  return {
    ...state,
    isPrivate: !state.isPrivate,
  };
};

export const toggleSystemMode = (state: BasketState): BasketState => {
  const { selections, isSystem } = state;
  const nextIsSystem = !isSystem;
  const nextSelections = nextIsSystem ? selections : [selections[0]];
  return {
    ...state,
    isSystem: nextIsSystem,
    selections: nextSelections,
  };
};

export const copySystem = (state: BasketState, action: CopySystemGridAction): BasketState => {
  const { selections } = state;
  const { index } = action.payload;
  const nextSelections = [...selections];
  if (selections[index]) {
    nextSelections.push(selections[index]);
    return {
      ...state,
      selections: nextSelections,
    };
  }
  return state;
};

export const removeSystem = (state: BasketState, action: RemoveSystemGridAction): BasketState => {
  const { selections } = state;
  const { index } = action.payload;
  let nextSelections = [...selections];
  nextSelections.splice(index, 1);
  const nextIsSystem = nextSelections.length > 0;

  if (nextSelections.length === 0) {
    nextSelections = [selections[0]];
  }
  return {
    ...state,
    isSystem: nextIsSystem,
    selections: nextSelections,
  };
};

export const copyGrid = (state: BasketState, action: CopyGridAction): BasketState => {
  const { grid } = action.payload;
  const { isSystem, strGridSelection, systemStrGridSelections, poolId, isPrivate } = grid;
  return {
    ...state,
    poolId,
    gridReference: null,
    gridType: GridTypes.PERSONAL,
    betAmount: '',
    isPrivate: !!isPrivate,
    isSystem: !!isSystem,
    selections: isSystem && systemStrGridSelections ? systemStrGridSelections : [strGridSelection],
    isSubmitting: false,
    isValidated: false,
    estr: null,
  };
};

export default createReducer(initialState, {
  [GridsActionTypes.COPY_SYSTEM_GRID]: copySystem,
  [GridsActionTypes.CHECK]: check,
  [GridsActionTypes.INIT_BASKET]: initBasket,
  [GridsActionTypes.INIT_PUBLIC_BASKET]: initPublicBasket,
  [GridsActionTypes.INIT_HISTORY_BASKET]: initHistoryBasket,
  [GridsActionTypes.SET_BASKET_PENDING]: setBasketPending,
  [GridsActionTypes.SET_BASKET_VALIDATED]: setBasketValidated,
  [GridsActionTypes.SET_BASKET_ERROR]: setBasketError,
  [GridsActionTypes.CLEAR_CHECKS]: clearChecks,
  [GridsActionTypes.FILL_RANDOMLY]: fillRandomly,
  [GridsActionTypes.SWITCH_TYPE]: switchType,
  [GridsActionTypes.SET_STAKE]: setStake,
  [GridsActionTypes.TOGGLE_PRIVATE_MODE]: togglePrivateMode,
  [GridsActionTypes.TOGGLE_SYSTEM_MODE]: toggleSystemMode,
  [GridsActionTypes.REMOVE_SYSTEM_GRID]: removeSystem,
  [GridsActionTypes.CLEAR_BASKET]: setBasketClear,
  [GridsActionTypes.COPY_GRID]: copyGrid,
  [GridsActionTypes.FILL_PRECHECKS]: fillPrechecks,
});
